import { useState, useMemo } from 'react'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

function getCardBorderColor(closeBBL) {
    if (closeBBL >= 250) return '#DC2626'
    if (closeBBL >= 200) return '#F59E0B'
    return '#1A7A3D'
}

function TankDetails({ tanks }) {
    const { theme } = useTheme()
    const isDark = theme === 'dark'

    const [search, setSearch] = useState('')
    const [sortBy, setSortBy] = useState('volDesc')
    const [historyModal, setHistoryModal] = useState(null) // { tankId, tankName, parentUnitName }
    const [historyData, setHistoryData] = useState(null)
    const [historyLoading, setHistoryLoading] = useState(false)

    // Filter and sort
    const filteredSorted = useMemo(() => {
        let result = tanks || []
        if (search) {
            const s = search.toLowerCase()
            result = result.filter(t =>
                t.parentUnitName?.toLowerCase().includes(s) ||
                t.tankName?.toLowerCase().includes(s)
            )
        }
        const sortFns = {
            fillDesc: (a, b) => (b.percentFull || 0) - (a.percentFull || 0),
            fillAsc: (a, b) => (a.percentFull || 0) - (b.percentFull || 0),
            volDesc: (a, b) => (b.closeBBL || 0) - (a.closeBBL || 0),
            volAsc: (a, b) => (a.closeBBL || 0) - (b.closeBBL || 0),
            nameAsc: (a, b) => (a.parentUnitName || '').localeCompare(b.parentUnitName || ''),
            daysUntilFull: (a, b) => (a.daysUntilFull ?? 9999) - (b.daysUntilFull ?? 9999),
        }
        return [...result].sort(sortFns[sortBy] || sortFns.fillDesc)
    }, [tanks, search, sortBy])

    // History modal fetch
    const openHistory = async (tank) => {
        setHistoryModal(tank)
        setHistoryLoading(true)
        setHistoryData(null)
        try {
            const res = await fetch(`${apiConfig.baseUrl}/tanks/history/${tank.tankId}`)
            if (res.ok) setHistoryData(await res.json())
        } catch (e) {
            /* ignore */
        } finally {
            setHistoryLoading(false)
        }
    }

    return (
        <div className="space-y-4">
            {/* Controls bar */}
            <div className="flex flex-wrap gap-2 items-center">
                <input
                    type="text"
                    placeholder="Search lease..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                        isDark
                            ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    }`}
                />
                <select
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                        isDark
                            ? 'bg-slate-800 border-slate-700 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                    }`}
                >
                    <option value="fillDesc">Fill % (High to Low)</option>
                    <option value="fillAsc">Fill % (Low to High)</option>
                    <option value="volDesc">Volume (High to Low)</option>
                    <option value="volAsc">Volume (Low to High)</option>
                    <option value="nameAsc">Lease Name (A to Z)</option>
                    <option value="daysUntilFull">Days Until Full</option>
                </select>
                <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {filteredSorted.length} tanks
                </span>
            </div>

            {/* Card grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {filteredSorted.map(tank => {
                    const borderColor = getCardBorderColor(tank.closeBBL)
                    return (
                        <button
                            key={tank.tankId}
                            onClick={() => openHistory(tank)}
                            className={`text-left rounded-lg border p-3 transition hover:ring-1 hover:ring-blue-500 ${
                                isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                            }`}
                            style={{ borderLeftWidth: '4px', borderLeftColor: borderColor }}
                        >
                            <div className="flex justify-between items-start">
                                <div className="min-w-0 flex-1">
                                    <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {tank.parentUnitName}
                                    </p>
                                    <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {tank.tankName}
                                    </p>
                                </div>
                                <span className="text-xl font-bold ml-2 shrink-0" style={{ color: borderColor }}>
                                    {tank.percentFull?.toFixed(1)}%
                                </span>
                            </div>
                            <div className="mt-2">
                                <div className={`h-2 rounded-full ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}>
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{
                                            width: `${Math.min(tank.percentFull || 0, 100)}%`,
                                            backgroundColor: borderColor,
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={`mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span>{tank.closeBBL?.toFixed(0)} / {tank.capacityBBL?.toFixed(0)} BBL</span>
                                <span>{tank.availableSpaceBBL?.toFixed(0)} avail</span>
                                {tank.daysUntilFull != null && <span>~{tank.daysUntilFull}d until full</span>}
                                {tank.lastHaulDate && <span>Hauled {new Date(tank.lastHaulDate).toLocaleDateString()}</span>}
                            </div>
                            {tank.purchaser && (
                                <span className={`inline-block mt-1.5 px-2 py-0.5 text-xs font-medium rounded ${
                                    tank.purchaser === 'RedBud'
                                        ? 'bg-red-900/30 text-red-400'
                                        : 'bg-blue-900/30 text-blue-400'
                                }`}>
                                    {tank.purchaser}
                                </span>
                            )}
                        </button>
                    )
                })}
            </div>

            {/* History Modal */}
            {historyModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                    onClick={() => setHistoryModal(null)}
                >
                    <div
                        className={`w-full max-w-2xl mx-4 rounded-lg border p-4 ${
                            isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'
                        }`}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <div>
                                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {historyModal.parentUnitName}
                                </h3>
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {historyModal.tankName} - 30 Day History
                                </p>
                            </div>
                            <button
                                onClick={() => setHistoryModal(null)}
                                className={`text-lg ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                            >
                                x
                            </button>
                        </div>
                        {historyLoading ? (
                            <div className="flex justify-center py-8">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                            </div>
                        ) : historyData ? (
                            <ResponsiveContainer width="100%" height={250}>
                                <AreaChart data={historyData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }}
                                        tickFormatter={d => {
                                            const dt = new Date(d)
                                            return `${dt.getMonth() + 1}/${dt.getDate()}`
                                        }}
                                    />
                                    <YAxis tick={{ fontSize: 10, fill: isDark ? '#94a3b8' : '#64748b' }} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1e293b' : '#fff',
                                            border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                        }}
                                        formatter={(v) => [`${v?.toFixed(1)} BBL`]}
                                        labelFormatter={d => new Date(d).toLocaleDateString()}
                                    />
                                    <Area type="monotone" dataKey="closeBBL" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                                </AreaChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-center text-gray-500 py-8">No history data available</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default TankDetails
